import gsap from "gsap";
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import type Camera from "../Camera";
import Experience from "../Experience";
import type Resources from "../utils/Resources";

export class CoolS3D {
  public group = new THREE.Group();
  private experience: Experience;
  private camera: Camera;
  private resources: Resources;
  private modelRoot: THREE.Object3D | null = null;
  private readonly fitMargin = 0.9;
  private readonly scaleBias = 0.95;
  private moonLight?: THREE.DirectionalLight;

  constructor() {
    this.experience = new Experience();
    this.camera = this.experience.camera;
    this.resources = this.experience.resources;

    this.loadModel();
  }

  private loadModel() {
    const gltf = this.resources.get<GLTF>("coolSModel");
    if (!gltf) {
      console.warn("CoolS3D: GLB model 'coolSModel' not found in resources");
      return;
    }

    // Load moon textures
    const moonDiffuse =
      this.resources.get<THREE.Texture>("moonDiffuseTexture")!;
    const moonNormal = this.resources.get<THREE.Texture>("moonNormalTexture")!;
    const moonDisplacement = this.resources.get<THREE.Texture>(
      "moonDisplacementTexture"
    )!;
    const moonArm = this.resources.get<THREE.Texture>("moonArmTexture")!;

    // Configure textures for proper rendering
    moonDiffuse.colorSpace = THREE.SRGBColorSpace;
    moonDiffuse.wrapS = moonDiffuse.wrapT = THREE.RepeatWrapping;
    moonDiffuse.needsUpdate = true;

    moonNormal.colorSpace = THREE.LinearSRGBColorSpace;
    moonNormal.wrapS = moonNormal.wrapT = THREE.RepeatWrapping;
    moonNormal.needsUpdate = true;

    moonDisplacement.colorSpace = THREE.LinearSRGBColorSpace;
    moonDisplacement.wrapS = moonDisplacement.wrapT = THREE.RepeatWrapping;
    moonDisplacement.needsUpdate = true;

    moonArm.colorSpace = THREE.LinearSRGBColorSpace;
    moonArm.wrapS = moonArm.wrapT = THREE.RepeatWrapping;
    moonArm.needsUpdate = true;

    const sceneClone = gltf.scene.clone(true);
    sceneClone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Apply moon textures to the material
        // Dispose old material to prevent memory leaks
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => mat.dispose());
        } else {
          mesh.material.dispose();
        }

        const material = new THREE.MeshStandardMaterial({
          map: moonDiffuse,
          normalMap: moonNormal,
          displacementMap: moonDisplacement,
          displacementScale: 0.05,
          aoMap: moonArm,
          roughnessMap: moonArm,
          metalnessMap: moonArm,
          roughness: 1.0,
          metalness: 0.0,
          side: THREE.DoubleSide,
          opacity: 1,
          transparent: false,
          // depthTest: false,
          // alphaTest: 0.1,
          // transparent: true,
        });
        // material.depthFunc = THREE.EqualDepth;

        // Ensure the geometry has UV2 for AO map
        if (!mesh.geometry.attributes.uv2) {
          mesh.geometry.setAttribute(
            "uv2",
            mesh.geometry.attributes.uv.clone()
          );
        }

        mesh.material = material;
      }
    });

    // Orient the model so it stands upright facing the camera
    sceneClone.rotation.set(-Math.PI / 2, 0, 0);

    this.modelRoot = sceneClone;
    this.group.add(sceneClone);
    this.experience.scene.add(this.group);
    this.centerAndFit();
    
    
  }

  public resize() {
    // Don't reset position/scale when in moon mode

    this.centerAndFit();
  }

  public setVisibility(visible: boolean) {
    this.group.visible = visible;
  }

  private centerAndFit() {
    if (!this.modelRoot) return;

    this.group.scale.setScalar(1);
    this.group.position.set(0, 0, 0);

    const box = new THREE.Box3().setFromObject(this.group);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    this.group.children.forEach((child) => child.position.sub(center));

    const vis = this.camera.getVisibleSizeAtZ(0);
    const targetW = vis.width * this.fitMargin;
    const targetH = vis.height * this.fitMargin;
    const sX = size.x > 0 ? targetW / size.x : 1;
    const sY = size.y > 0 ? targetH / size.y : 1;
    const scale = Math.min(sX, sY) * this.scaleBias;
    this.group.scale.setScalar(scale);
    this.group.position.z = 0.05;
  }

  public showUp(
    earthPosition: THREE.Vector3,
    duration: number = 9,
    onFinish?: () => void
  ) {
    // Calculate viewport dimensions at Earth's z position
    const camera = this.camera.instance;
    const distance = Math.abs(camera.position.z - earthPosition.z);
    const vFov = (camera.fov * Math.PI) / 180;
    const visibleHeight = 2 * Math.tan(vFov / 2) * distance;
    const visibleWidth = visibleHeight * camera.aspect;

    // Calculate scale to be proportional to Earth (smaller, like moon size)
    // Adjust the 0.1 multiplier to change size (0.03 = very small, 0.1 = medium, 0.2 = larger)
    const moonScale = (Math.max(visibleWidth, visibleHeight) / 100) * 2.3;

    this.setVisibility(true);

    // Create directional light from CoolS3D pointing at Earth
    this.moonLight = new THREE.DirectionalLight(0xffffff, 0); // Start at 0 intensity
    this.moonLight.castShadow = false;
    this.experience.scene.add(this.moonLight);
    this.experience.scene.add(this.moonLight.target);

    // Start position: behind Earth (same position but further back in z)
    this.group.position.set(
      earthPosition.x,
      earthPosition.y,
      earthPosition.z - 200 // Behind Earth
    );
    this.group.scale.setScalar(moonScale);

    // End position: top-left corner of Earth (relative to Earth and viewport)
    // Calculate offsets proportional to viewport size for consistency across screen sizes
    const viewportSize = Math.max(visibleWidth, visibleHeight);
    const xyOffset = viewportSize * 0.12; // Adjust this to move more/less from Earth center

    // Z offset should be proportional to viewport width (not max) for better depth scaling
    // Smaller multiplier for small screens, larger for wide screens

    const endX = earthPosition.x - 50 - xyOffset; // Left of Earth center
    const endY = earthPosition.y + 50 + xyOffset; // Top of Earth center
    const endZ = earthPosition.z - 200; // Closer to camera (less negative)
    // Create rotation animation timeline
    const timeline = gsap.timeline({
      onComplete: () => {
        if (onFinish) onFinish();
      },
    });

    // Animate position along an arc (like moon orbiting)
    timeline.to(
      this.group.position,
      {
        x: endX,
        y: endY,
        z: endZ,
        duration: duration,
        ease: "power2.inOut",
        onUpdate: () => {
          // Update light position to follow CoolS3D
          if (this.moonLight) {
            this.moonLight.position.copy(this.group.position);
            this.moonLight.target.position.copy(earthPosition);
          }
        },
      },
      0
    );

    // Animate light intensity
    if (this.moonLight) {
      timeline.to(
        this.moonLight,
        {
          intensity: 1.5,
          duration: duration,
          ease: "power2.inOut",
        },
        0
      );
    }
  }
}

export default CoolS3D;
